import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';

import { toggleModalWithTimer, toggleSpinner } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { changeFieldProperty, changeFormFieldValue, setFieldError, updateFields, updateFieldValues } from 'apps/auth/actions/formAction';
import { Button } from 'apps/auth/components/common/Button/Button';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { FixedBottomPanel } from 'apps/auth/components/common/Layout/FixedBottomPanel/FixedBottomPanel';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import PopupButton from 'apps/auth/components/common/Popup/PopupButton';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import SimplePopup from 'apps/auth/components/common/Popup/SimplePopup';
import InputAutoDetect from 'apps/auth/components/form/InputAutoDetect';
import Form from 'apps/auth/components/form-multi/Form';
import { clearLeadingSpace, getEmptyMandatoryOrFieldsWithError } from 'apps/auth/components/form-multi/utils';
import EqualNameAndNameFPopup from 'apps/auth/components/send/popups/errors/EqualNameAndNameFPopup';
import ModalError from 'apps/auth/components/utils/ModalError';
import ModalNew from 'apps/auth/components/utils/ModalNew';
import { NAME, NAME_F, NATIONALITY, RESIDENT_COUNTRY } from 'apps/auth/constants/fieldsName';
import { PROFILE_FORM_NAME } from 'apps/auth/constants/formName';
import { AUTH_PROFILE_FIELDS, AUTH_PROFILE_PERSONAL_FIELDS_SAVE } from 'apps/auth/constants/UrlConstants';
import { useExtra, useFields, usePageMessages } from 'apps/auth/hooks/selectors';
import { getFieldsToEdit, isFieldDataUnchanged } from 'apps/auth/services/ProfileService';

const ProfilePersonalInfoEdit = (props) => {
    const { continueButton } = usePageMessages();
    const { phoneCountry, profileFieldsGroups, headlines } = useExtra();
    const formName = PROFILE_FORM_NAME;
    const fields = useFields(formName);
    const [step, setStep] = useState(0);
    const [currentFields, setCurrentFields] = useState(getFieldsToEdit(profileFieldsGroups, step));
    const [disabledSubmitButton, setDisabledSubmitButton] = useState(true);
    const modalRef = useRef(null);
    const popupRef = useRef(null);
    const submitRef = useRef(null);
    const title = headlines.length > 0 ? headlines[step]?.title : '';
    const subtitle = headlines.length > 0 ? headlines[step]?.subtitle : '';

    useEffect(() => {
        const errorFields = getEmptyMandatoryOrFieldsWithError(fields, false);
        setDisabledSubmitButton(errorFields?.length > 0);
    }, [fields]);

    const switchToNextScreen = () => {
        const nextStep = step + 1;
        const nextFields = getFieldsToEdit(profileFieldsGroups, nextStep);
        if (Array.isArray(nextFields) && nextFields.length > 0) {
            setStep(nextStep);
            setCurrentFields(nextFields);
        } else {
            props.toggleSpinner(true);
            window.location.href = AUTH_PROFILE_FIELDS;
        }
    };

    const onContinueButtonClick = () => {
        if (isFieldDataUnchanged(fields, currentFields)) {
            switchToNextScreen();
        } else {
            submitRef.current.click();
        }
    };

    const submitHandler = (fields, json) => {
        if (json?.errors && Object.keys(json?.errors)?.length > 0) {
            Object.keys(json.errors).forEach((error) => {
                if (json.errors[error] && json.errors[error]?.body) {
                    const errorMessage = json.errors[error].body;
                    const buttonMessage = json.messages?.contactSupport;
                    modalRef.current?.open(
                        <ModalError
                            canCloseModal
                            isIconClose
                            linkTo={AUTH_PROFILE_FIELDS}
                            mainText={errorMessage}
                            primaryButtonCaption={buttonMessage}
                            primaryButtonOnClick={() => props.toggleChat(true)}
                        />,
                    );
                }
            });
        } else if (json?.extra?.sameNameNameFPopup) {
            popupRef?.current?.open(<EqualNameAndNameFPopup popupRef={popupRef} />);
            props.setFieldError(formName, NAME_F, json.extra?.errors[NAME_F]);
        } else if (json?.extra?.suggestionName) {
            popupRef?.current?.open(
                <SimplePopup popupRef={popupRef} text={<DangerouslyHtml dangerouslyHtml={json.messages?.transliterationTextPopup} />}>
                    <PopupButton
                        iconName="arrow-back-circular"
                        text={json.messages?.goBackAndEditButton}
                        onClick={() => {
                            popupRef?.current?.close();
                        }}
                    />
                    <PopupButton
                        iconName="arrow-forward"
                        text={json.messages?.acceptAndContinueButton}
                        onClick={() => {
                            const fields = {
                                name: json.extra.suggestionName,
                                name_f: json.extra.suggestionNameF,
                            };
                            props.updateFieldValues(formName, fields).then(() => {
                                submitRef.current?.click();
                                popupRef?.current?.close();
                            });
                        }}
                    />
                </SimplePopup>,
            );
        } else if (json?.extra?.errors && Object.keys(json?.extra?.errors).length) {
            setDisabledSubmitButton(true);
            if (json?.extra?.backendDidNotResponseForTransliteration) {
                let nameField = fields[NAME];
                nameField.placeholder = json.messages?.placeHolderForName;
                nameField.checkRegexp = json.extra?.latinRegexForName;
                let nameFField = fields[NAME_F];
                nameFField.checkRegexp = json.extra?.latinRegexForName;
                nameFField.placeholder = json.messages?.placeHolderForNameF;
                props
                    .updateFields(formName, { nameField, nameFField })
                    .then(Object.keys(json.extra.errors).forEach((name) => props.setFieldError(formName, name, json.extra.errors[name])));
            } else {
                Object.keys(json.extra.errors).forEach((name) => props.setFieldError(formName, name, json.extra.errors[name]));
            }
        } else if (json?.messages) {
            const successMessage = json.messages.successMessage;
            if (successMessage) {
                props.toggleModalWithTimer(true, successMessage, () => {
                    switchToNextScreen();
                    props.toggleModalWithTimer(false, '', false);
                });
            }
        }
    };

    const beforeSubmit = () =>
        clearLeadingSpace(
            currentFields.map((f) => fields[f.name]),
            formName,
            props.changeFormFieldValue,
        );

    const clearFieldsOnCountrySelect = () => {
        const fieldsToClear = getFieldsToEdit(profileFieldsGroups, step);
        fieldsToClear.forEach((f) => {
            if (f.name !== RESIDENT_COUNTRY) {
                props.changeFormFieldValue(formName, null, f.name);
            }
        });
    };

    return (
        <>
            <HeaderPanel title={title} subtitle={subtitle} />
            <Form
                action={AUTH_PROFILE_PERSONAL_FIELDS_SAVE}
                id="profile_form_id"
                formName={formName}
                getCanonicalPath={true}
                checkNotVisibleField={false}
                fieldsForSubmit={currentFields}
                beforeSubmit={beforeSubmit}
                onSubmitHandler={submitHandler}
            >
                <fieldset>
                    {currentFields.map((f) => {
                        const field = fields[f.name];
                        return (
                            <fieldset key={`${formName}-fields-${field.name}`}>
                                <InputAutoDetect
                                    formName={formName}
                                    field={field}
                                    country={phoneCountry}
                                    firstInputNotFocused
                                    clearFieldsOnCountrySelect={clearFieldsOnCountrySelect}
                                    hasCountryItemPanel={field.name === RESIDENT_COUNTRY || field.name === NATIONALITY}
                                />
                            </fieldset>
                        );
                    })}
                </fieldset>
                <FixedBottomPanel isRightAligned>
                    <Button
                        type="button"
                        raised={true}
                        caption={continueButton}
                        title={continueButton}
                        disabled={disabledSubmitButton}
                        onClick={() => {
                            onContinueButtonClick();
                        }}
                    />
                    <Button type="submit" variant="hidden" ref={submitRef} />
                </FixedBottomPanel>
            </Form>
            <ModalNew ref={modalRef} />
            <PopupNew ref={popupRef} />
        </>
    );
};
export default connect(null, {
    toggleChat,
    toggleModalWithTimer,
    changeFieldProperty,
    toggleSpinner,
    setFieldError,
    updateFieldValues,
    updateFields,
    changeFormFieldValue,
})(ProfilePersonalInfoEdit);
